import type { FlowRoute, OnboardingProgress } from './flow-route-types.js';
import { normalizePath } from './router.js';

export function stepsOf(steps: readonly FlowRoute[]): FlowRoute[] {
  return steps.filter((step) => step.kind === 'step');
}

/** One source of truth for step numbering, so the rail and the heading cannot disagree. */
export function stepNumberFor(steps: readonly FlowRoute[], route: FlowRoute): number {
  return stepsOf(steps).findIndex((candidate) => candidate.path === route.path) + 1;
}

export function totalSteps(steps: readonly FlowRoute[]): number {
  return stepsOf(steps).length;
}

export function firstIncompleteStep(
  steps: readonly FlowRoute[],
  progress: OnboardingProgress,
): FlowRoute | null {
  return stepsOf(steps).find((step) => !step.isComplete(progress)) ?? null;
}

/** An step is reachable once every earlier step is complete. Entry and dashboard always are. */
export function isRouteReachable(
  steps: readonly FlowRoute[],
  route: FlowRoute,
  progress: OnboardingProgress,
): boolean {
  if (route.kind !== 'step') {
    return true;
  }

  const onboardingSteps = stepsOf(steps);
  const index = onboardingSteps.findIndex((candidate) => candidate.path === route.path);

  if (index <= 0) {
    return true;
  }

  return onboardingSteps.slice(0, index).every((step) => step.isComplete(progress));
}

export interface EntryPathResolution {
  path: string;
  redirected: boolean;
}

/**
 * Normalizes a requested path without moving the merchant away from it.
 *
 * Missing information is handled at explicit action points instead of redirecting the page back to
 * the first incomplete step.
 */
export function resolveEntryPath(input: {
  requestedPath: string;
  steps: readonly FlowRoute[];
  progress: OnboardingProgress;
}): EntryPathResolution {
  void input.steps;
  void input.progress;

  return { path: normalizePath(input.requestedPath), redirected: false };
}
